import React from "react";
import ScoreCircle from "../../../../utils/ScoreCircle";
import InfoTip from "../../../../utils/InfoTip";
import { LuLink, LuMail, LuMessagesSquare, LuTag, LuUpload } from "react-icons/lu";
import { FaFacebookMessenger, FaLinkedin, FaViber, FaWhatsapp } from "react-icons/fa";
import { CANDIDATE_SOURCE, formatCandidateSource, normalizeCandidateSource } from "../../../../constants/candidateSource";
import { JOB_STAGES } from "../../../../utils/candidatesV2Constants";
import { NOT_ASSIGNED_TITLE, initialsOf, openCandidateProfile } from "../constants";
import { StageStatusIcon } from "../icons";
import HiringStatusCell from "./HiringStatusCell";
import RowActionsCell from "./RowActionsCell";

const SOURCE_ICONS = {
  [CANDIDATE_SOURCE.CHATBOT]: <LuMessagesSquare size={12} />,
  [CANDIDATE_SOURCE.RESUME_UPLOAD]: <LuUpload size={12} />,
  [CANDIDATE_SOURCE.INVITED_CANDIDATE]: <LuMail size={12} />,
  [CANDIDATE_SOURCE.APPLY_PAGE]: <LuLink size={12} />,
  [CANDIDATE_SOURCE.FACEBOOK_MESSENGER]: <FaFacebookMessenger size={12} />,
  [CANDIDATE_SOURCE.WHATSAPP]: <FaWhatsapp size={12} />,
  [CANDIDATE_SOURCE.LINKEDIN]: <FaLinkedin size={12} />,
  [CANDIDATE_SOURCE.VIBER]: <FaViber size={12} />,
};

const StageScoreValue =({ stage, data }) => {
  if (!data) return <span className="stage-cell-dash">—</span>;
  if (stage.name === "Assessment" && (data.score != null || data.status === "Completed")) return <StageStatusIcon status="Completed" />;
  if (data.score != null) return <span className="stage-cell-score">{Math.round(data.score)}%</span>;
  if (data.status && data.status !== "Not Started") return <StageStatusIcon status={data.status} />;
  return <span className="stage-cell-dash">—</span>;
};

const CandidateRow = ({
  jobId, job, candidate, accessible, assignee, selected, onToggleSelect,
  showAssigneeColumn, canManageAssignments, onAssign, onQuickClear,
  hiringStatuses, statusLoading, statusMenu, onStatusPick,
  actionMenu, onAction,
  getCandidateStageScore, getDaysFromToday, disabled,
}) => {
  const rowClass = [selected ? "row-selected" : "", accessible ? "" : "row-locked"].filter(Boolean).join(" ");

  return (
    <tr className={rowClass} title={accessible ? undefined : NOT_ASSIGNED_TITLE}>
      <td className="td-check">
        <input type="checkbox" checked={selected} disabled={!accessible} onChange={() => accessible && onToggleSelect(job.id)} />
      </td>
      <td className="td-candidate">
        <strong className={`candidate-name-link${accessible ? "" : " disabled"}`}
          onClick={() => accessible && openCandidateProfile(jobId, job.id)}>
          {job.candidates}
        </strong>
        {(job.phone_number || candidate?.source) && (
          <div className="candidate-phone-v2 candidate-source-v2">
            {job.phone_number}
            {candidate?.source && (
              <InfoTip text={`Source: ${formatCandidateSource(candidate.source)}`} width="auto" className="candidate-source-icon-v2">
                {SOURCE_ICONS[normalizeCandidateSource(candidate.source)] || <LuTag size={12} />}
              </InfoTip>
            )}
          </div>
        )}
      </td>
      <td className="td-added">
        <div style={{ fontSize: "13px", fontWeight: 500 }}>{getDaysFromToday(job.createdAt)}</div>
        <div style={{ fontSize: "11px", color: "#8A9199", marginTop: 2 }}>{job.addedOn}</div>
      </td>
      <td className="td-resume"><ScoreCircle score={job.resumeScore} /></td>
      <td className="td-stages">
        <div className="stage-scores-grid">
          {JOB_STAGES.map((stage) => (
            <div key={stage.name} className="stage-score-cell">
              <span className="stage-cell-abbrev" title={stage.name}>{stage.abbrev}</span>
              <StageScoreValue stage={stage} data={getCandidateStageScore(candidate, stage.id)} />
            </div>
          ))}
        </div>
      </td>
      {showAssigneeColumn && (
        <td className="td-assignee">
          <div className="assignee-cell-v2">
            {assignee ? (
              <span className={`assignee-chip-v2${assignee.isMe ? " is-me" : ""}`} title={assignee.email || assignee.name}>
                <span className="assignee-avatar-v2">{initialsOf(assignee.name)}</span>
                {assignee.isMe ? "You" : assignee.name}
                {canManageAssignments && (
                  <button type="button" className="assignee-clear-v2" title="Remove assignment"
                    disabled={disabled} onClick={() => onQuickClear(candidate)}>×</button>
                )}
              </span>
            ) : (
              <span className="assignee-none-v2">Unassigned</span>
            )}
            {canManageAssignments && (
              <button type="button" className="assignee-change-v2" disabled={disabled} onClick={() => onAssign(candidate)}>
                {assignee ? "Change" : "Assign"}
              </button>
            )}
          </div>
        </td>
      )}
      <td className="td-status">
        <HiringStatusCell
          currentName={candidate?.hiring_status?.name || "In Progress"}
          hiringStatuses={hiringStatuses}
          isOpen={statusMenu.isOpen}
          menuPos={statusMenu.pos}
          onOpen={statusMenu.open}
          onClose={statusMenu.close}
          onPick={(status, fromName) => onStatusPick(job.id, status, fromName)}
          loading={statusLoading}
          disabled={disabled || !accessible}
        />
      </td>
      <td className="td-actions">
        <RowActionsCell
          isMenuOpen={actionMenu.isOpen}
          menuPos={actionMenu.pos}
          onOpenMenu={actionMenu.open}
          onCloseMenu={actionMenu.close}
          onAction={(key) => onAction(key, candidate)}
          disabled={disabled || !accessible}
        />
      </td>
    </tr>
  );
};

export default CandidateRow;
